import { useEffect } from 'react';
import { disableNetwork, enableNetwork, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { crearGestorDeRed, LATIDO_MS, SALTO_DE_LATIDO_MS } from '../utils/gestorDeRed';
import {
  anotar, anotarAgrupando, completar, resultadoDeReconexion, esErrorDeRed, ESPERA_SERVIDOR_MS,
} from '../utils/diagnosticoDeRed';

/**
 * La red de Firestore al ir y volver del segundo plano. La lógica y el porqué están en `utils/gestorDeRed.js`
 * (punto 10, 4-nov-2026): antes, en el iPhone, la red podía quedar APAGADA con la app en pantalla. Lo que se mide,
 * y dónde se guarda, en `utils/diagnosticoDeRed.js`.
 *
 * Un solo gestor para toda la app (lo monta `App`): la bandeja, el detalle del chat y el resto de oyentes
 * comparten `db`, así que reconectar una vez los despierta a todos.
 */
let gestor = null;

// ── LA MEDICIÓN ────────────────────────────────────────────────────────────────────
const CLAVE_DIAGNOSTICO = 'pux.diagnosticoRed';
/** Lo que el tirón espera al servidor antes de quitar «Actualizando…». */
const ESPERA_DEL_AVISO_MS = 8000;
/**
 * Las escrituras al servidor se juntan: a lo más una por minuto, y ninguna si la lista no cambió. Una reconexión
 * anota dos veces (al empezar y con su resultado), y una cascada de errores, muchas (tercera revisión, 🟡4).
 */
const ESPACIO_ENTRE_SUBIDAS_MS = 60000;
let ultimaSubida = null;
const INTERRUMPIDA = Symbol('interrumpida');
let ultimoDelServidorMs = null;
let oyentesMontados = 0;
const esperandoAlServidor = new Set();
/** `{ orgId, uid }` de quien tiene la bandeja abierta en una organización; sin él, sólo se guarda en el teléfono. */
let destino = null;
let subidaPendiente = null;

export function leerDiagnosticoDeRed() {
  try { return JSON.parse(window.localStorage.getItem(CLAVE_DIAGNOSTICO) || '[]'); } catch (e) { return []; }
}
function subirDiagnostico() {
  subidaPendiente = null;
  if (!destino) return;
  const { orgId, uid } = destino;
  const entradas = leerDiagnosticoDeRed();
  const huella = JSON.stringify(entradas);
  if (huella === ultimaSubida) return;
  ultimaSubida = huella;
  setDoc(
    doc(db, 'organizations', orgId, 'diagnosticoRed', uid),
    { entradas, actualizadoEn: serverTimestamp(), agente: String(navigator.userAgent || '').slice(0, 200) },
    { merge: true },
  ).catch((e) => { ultimaSubida = null; console.warn('[red] no se pudo subir el diagnóstico:', (e && e.message) || e); });
}
function guardarDiagnostico(lista) {
  try { window.localStorage.setItem(CLAVE_DIAGNOSTICO, JSON.stringify(lista)); } catch (e) { /* sin almacenamiento: no se mide */ }
  if (destino && !subidaPendiente) subidaPendiente = setTimeout(subirDiagnostico, ESPACIO_ENTRE_SUBIDAS_MS);
}
export function borrarDiagnosticoDeRed() { guardarDiagnostico([]); }
let siguienteId = 0;
const nuevoId = () => `${Date.now().toString(36)}-${(siguienteId += 1)}`;
export function anotarEnDiagnosticoDeRed(entrada) {
  guardarDiagnostico(anotarAgrupando(leerDiagnosticoDeRed(), { id: nuevoId(), en: new Date().toISOString(), ...entrada }));
}

/** La bandeja dice a nombre de quién y de qué organización se sube la medición (null al salir). */
export function destinoDelDiagnosticoDeRed(nuevo) { destino = nuevo && nuevo.orgId && nuevo.uid ? nuevo : null; }

/** Cierra las esperas en curso: la app se oculta o sale de la bandeja, y lo que pase después no es de esa reconexión. */
function interrumpirEsperas() {
  esperandoAlServidor.forEach((resolver) => resolver(INTERRUMPIDA));
  esperandoAlServidor.clear();
}
/** La bandeja avisa si su oyente está montado: sin él, una reconexión no tiene con qué medirse. */
export function oyenteDeBandejaMontado(montado) {
  oyentesMontados = Math.max(0, oyentesMontados + (montado ? 1 : -1));
  if (!montado) interrumpirEsperas();
}
/** La bandeja avisa cada snapshot del SERVIDOR (`metadata.fromCache === false`). */
export function marcarSnapshotDelServidor() {
  ultimoDelServidorMs = Date.now();
  esperandoAlServidor.forEach((resolver) => resolver(ultimoDelServidorMs));
  esperandoAlServidor.clear();
}
/** Cuándo llegó el servidor desde `desdeMs`; null si no llegó en `topeMs`; `INTERRUMPIDA` si se cortó la espera. */
function esperarAlServidor(desdeMs, topeMs) {
  if (ultimoDelServidorMs != null && ultimoDelServidorMs >= desdeMs) return Promise.resolve(ultimoDelServidorMs);
  return new Promise((resolve) => {
    const resolver = (valor) => { clearTimeout(tope); resolve(valor); };
    const tope = setTimeout(() => {
      esperandoAlServidor.delete(resolver);
      // Vencer con mucho retraso es que la app estuvo congelada (iOS no siempre avisa): no se sabe, no es «sin servidor».
      resolve(Date.now() - desdeMs > topeMs + SALTO_DE_LATIDO_MS ? INTERRUMPIDA : null);
    }, topeMs);
    esperandoAlServidor.add(resolver);
  });
}

function registrar(evento, datos) {
  const { listo, ...resto } = datos || {};
  console.info(`[red] ${evento}`, resto);
  if (evento === 'reconexion') {
    const id = nuevoId();
    const conOyente = oyentesMontados > 0;
    guardarDiagnostico(anotar(leerDiagnosticoDeRed(), {
      id, en: new Date().toISOString(), evento, tipo: resto.tipo, accion: resto.accion, motivo: resto.motivo,
      fueraMs: resto.fueraMs, resultado: conOyente ? 'esperando' : 'sin-oyente',
    }));
    if (!conOyente) return;
    // Se espera al servidor desde que la reconexión TERMINÓ: lo que el canal viejo dejó en vuelo no la prueba.
    Promise.resolve(listo).then(() => {
      const desdeMs = Date.now();
      return esperarAlServidor(desdeMs, ESPERA_SERVIDOR_MS).then((valor) => guardarDiagnostico(completar(
        leerDiagnosticoDeRed(), id,
        resultadoDeReconexion({ desdeMs, servidorEnMs: valor === INTERRUMPIDA ? null : valor, conOyente, interrumpida: valor === INTERRUMPIDA }),
      )));
    });
  } else if (evento === 'error') {
    anotarEnDiagnosticoDeRed({ evento, paso: resto.paso, error: resto.error });
  }
}

/** Para «tirar para actualizar»: reconecta y espera al servidor (con tope). Enseguida si no hay gestor. */
export async function reconectarFirestore() {
  if (!gestor) return;
  await gestor.reconectarAhora().listo;
  if (oyentesMontados > 0) await esperarAlServidor(Date.now(), ESPERA_DEL_AVISO_MS);
}

export function useFirestoreIdleManager() {
  useEffect(() => {
    const g = crearGestorDeRed({
      apagar: () => disableNetwork(db),
      encender: () => enableNetwork(db),
      esVisible: () => document.visibilityState === 'visible',
      registrar,
    });
    gestor = g;

    const alOcultarse = () => { interrumpirEsperas(); g.alOcultar(); };
    const alCambiarVisibilidad = () => (document.visibilityState === 'hidden' ? alOcultarse() : g.alVolver('visible'));
    const alMostrar = (e) => g.alVolver('pageshow', { persisted: !!(e && e.persisted) });
    const alEnfocar = () => g.alVolver('focus');
    const alConectar = () => g.alVolver('online');
    // Lo que el SDK no entrega a ningún oyente (p. ej. iOS perdió IndexedDB): también se anota.
    const alFallarSuelto = (e) => {
      const mensaje = (e && (e.reason?.message || e.reason || e.message)) || '';
      if (esErrorDeRed(mensaje)) anotarEnDiagnosticoDeRed({ evento: 'error-suelto', error: String(mensaje).slice(0, 200) });
    };

    document.addEventListener('visibilitychange', alCambiarVisibilidad);
    window.addEventListener('pageshow', alMostrar);
    window.addEventListener('pagehide', alOcultarse);
    window.addEventListener('focus', alEnfocar);
    window.addEventListener('online', alConectar);
    window.addEventListener('unhandledrejection', alFallarSuelto);
    window.addEventListener('error', alFallarSuelto);
    const latido = setInterval(() => g.latido(), LATIDO_MS);

    return () => {
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      window.removeEventListener('pageshow', alMostrar);
      window.removeEventListener('pagehide', alOcultarse);
      window.removeEventListener('focus', alEnfocar);
      window.removeEventListener('online', alConectar);
      window.removeEventListener('unhandledrejection', alFallarSuelto);
      window.removeEventListener('error', alFallarSuelto);
      clearInterval(latido);
      g.detener();
      if (gestor === g) gestor = null;
    };
  }, []);
}
